import type { ReactNode } from "react";

export interface DataColumn<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
  className?: string;
  align?: "left" | "right" | "center";
  nowrap?: boolean;
}

interface DataTableProps<T> {
  columns: DataColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  minWidth?: string;
  empty?: ReactNode;
  /**
   * Enables the leading checkbox column. Supply `selectedKeys` plus the two
   * handlers to make rows selectable (used for bulk actions).
   */
  selectable?: boolean;
  selectedKeys?: string[];
  onToggleRow?: (key: string) => void;
  onToggleAll?: (keys: string[], checked: boolean) => void;
}

const alignClass = (align: DataColumn<unknown>["align"]) =>
  align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

const checkboxClass =
  "h-4 w-4 cursor-pointer rounded border-background-300 text-primary-600 focus:ring-primary-400";

export default function DataTable<T>({
  columns,
  rows,
  rowKey,
  minWidth = "900px",
  empty,
  selectable = false,
  selectedKeys = [],
  onToggleRow,
  onToggleAll,
}: DataTableProps<T>) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
        {empty ?? <p className="text-sm text-foreground-500">Nothing to show here yet.</p>}
      </div>
    );
  }

  const selected = new Set(selectedKeys);
  const visibleKeys = rows.map((row) => rowKey(row));
  const allSelected = visibleKeys.length > 0 && visibleKeys.every((key) => selected.has(key));
  const someSelected = visibleKeys.some((key) => selected.has(key));

  return (
    <div className="overflow-x-auto rounded-lg border border-background-200 bg-background-50">
      <table className="w-full text-sm" style={{ minWidth }}>
        <thead>
          <tr className="bg-background-100/70 text-left text-xs uppercase tracking-wide text-foreground-500">
            {selectable && (
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  aria-label="Select all rows"
                  className={checkboxClass}
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = !allSelected && someSelected;
                  }}
                  onChange={(e) => onToggleAll?.(visibleKeys, e.target.checked)}
                />
              </th>
            )}
            {columns.map((c) => (
              <th key={c.key} className={`px-4 py-3 font-semibold ${alignClass(c.align)} ${c.nowrap ? "whitespace-nowrap" : ""}`}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key = rowKey(row);
            const isSelected = selected.has(key);
            return (
              <tr
                key={key}
                className={`border-t border-background-100 ${isSelected ? "bg-primary-50/60" : "hover:bg-background-100/40"}`}
              >
                {selectable && (
                  <td className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label="Select row"
                      className={checkboxClass}
                      checked={isSelected}
                      onChange={() => onToggleRow?.(key)}
                    />
                  </td>
                )}
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-4 py-3 ${alignClass(c.align)} ${c.nowrap ? "whitespace-nowrap" : ""} ${c.className ?? ""}`}
                  >
                    {c.render ? c.render(row) : ((row as Record<string, unknown>)[c.key] as ReactNode)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}